import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { createEmployee, updateEmployee, deleteEmployee } from '../services/firestoreService';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { DEPARTMENTS } from '../utils/constants';
import {
  formatCurrency,
  formatDate,
  parseDateInputValue,
  getEmployeeWorkHistory,
  downloadCsvFile,
  toMoney
} from '../utils/helpers';
import toast from 'react-hot-toast';

const Employees = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { employees, loadingEmployees, expenses, invoices, quotations } = useApp();
  
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    department: '',
    startWorkDate: ''
  });

  const formatStartDateField = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.slice(0, 10);
    if (value?.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
    if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingEmployee(null);
    setFormData({ name: '', phone: '', department: '', startWorkDate: '' });
    setShowModal(true);
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      phone: employee.phone,
      department: employee.department,
      startWorkDate: formatStartDateField(employee.startWorkDate)
    });
    setShowModal(true);
  };

  const openDeleteModal = (employee) => {
    setEditingEmployee(employee);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim() || !formData.department) {
      toast.error('Fadlan buuxi dhammaan meelaha');
      return;
    }

    setSubmitting(true);

    const payload = {
      name: formData.name,
      phone: formData.phone,
      department: formData.department,
      startWorkDate: parseDateInputValue(formData.startWorkDate),
      auditActor
    };

    let result;
    if (editingEmployee) {
      result = await updateEmployee(editingEmployee.id, payload);
    } else {
      result = await createEmployee(payload);
    }

    if (result.success) {
      toast.success(editingEmployee ? 'Waa la cusbooneysiiyay' : 'Shaqaale cusub waa la daray');
      setShowModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }

    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const result = await deleteEmployee(editingEmployee.id);
    if (result.success) {
      toast.success('Shaqaalaha waa la tirtiray');
      setShowDeleteModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const columns = [
    {
      key: 'name',
      label: 'Magaca',
      render: (value) => <span className="font-medium">{value}</span>
    },
    {
      key: 'phone',
      label: 'Telefoon'
    },
    {
      key: 'department',
      label: 'Qaybta'
    },
    {
      key: 'jobsHandled',
      label: 'Shaqooyin',
      render: (value) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
          {value || 0}
        </span>
      )
    },
    {
      key: 'revenueInvolved',
      label: 'Expenses',
      render: (value) => (
        <span className="font-medium text-green-600">{formatCurrency(value || 0)}</span>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); openEditModal(row); }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openDeleteModal(row); }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  const { page, setPage, pageSize, setPageSize, total, totalPages, pagedItems } = usePagination(
    employees,
    { persistKey: 'employees' }
  );

  const totalJobs = employees.reduce((sum, emp) => sum + (emp.jobsHandled || 0), 0);
  const totalRevenue = employees.reduce((sum, emp) => sum + (emp.revenueInvolved || 0), 0);

  const exportEmployeesCsv = () => {
    if (!employees.length) {
      toast.error('Shaqaale ma jiraan');
      return;
    }
    const headers = [
      'ID',
      'Magaca',
      'Telefoon',
      'Qaybta',
      'Bilaabay shaqada',
      'Shaqooyin (kharash)',
      'Kharash wadar ($)',
      'Shifts / guurimo',
      'Kharash records',
      'Kharash xiriir ($)',
      'Active',
      'La sameeyay',
      'User (sameeyay)',
      'La cusbooneysiiyay',
      'User (cusbooneysiiyay)'
    ];
    const rows = employees.map((emp) => {
      const hist = getEmployeeWorkHistory(emp, { expenses, invoices, quotations });
      const histExpenseTotal = hist.expenses.reduce((sum, e) => sum + toMoney(e.amount), 0);
      return [
        emp.id,
        emp.name,
        emp.phone,
        emp.department,
        formatDate(emp.startWorkDate),
        emp.jobsHandled || 0,
        toMoney(emp.revenueInvolved),
        hist.shifts.length,
        hist.expenses.length,
        histExpenseTotal,
        emp.isActive === false ? 'Maya' : 'Haa',
        formatDate(emp.createdAt),
        emp.createdByEmail || emp.createdByName || '',
        formatDate(emp.updatedAt),
        emp.updatedByEmail || emp.updatedByName || ''
      ];
    });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`shaqaalaha-all-${stamp}.csv`, headers, rows);
    toast.success(`CSV: ${employees.length} shaqaale`);
  };

  return (
    <div className="space-y-4">
      <PageHero
        accent="purple"
        eyebrow="Kooxda"
        title="Shaqaalaha"
        subtitle="Maamul shaqaalaha — riix saf si aad u aragto shaqada & kharashka"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={exportEmployeesCsv} icon={<span>📥</span>}>
              Soo deji CSV (dhammaan)
            </Button>
            <Button onClick={openAddModal} icon={<span>➕</span>}>
              Ku dar Shaqaale
            </Button>
          </div>
        }
        stats={[
          { label: 'Shaqaalaha', value: employees.length },
          { label: 'Shaqooyin', value: totalJobs },
          { label: 'Expenses', value: formatCurrency(totalRevenue) },
          { label: 'Celcelis / qof', value: employees.length > 0 ? Math.round(totalJobs / employees.length) : 0 }
        ]}
      />

      <Card padding={false}>
        <Table
          columns={columns}
          data={pagedItems}
          loading={loadingEmployees}
          emptyMessage="Shaqaale lama helin"
          onRowClick={(row) => navigate(`/admin/employees/${row.id}`)}
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingEmployee ? 'Wax ka bedel Shaqaalaha' : 'Ku dar Shaqaale Cusub'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Magaca"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Magaca shaqaalaha"
            required
          />
          <Input
            label="Telefoon"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="061XXXXXXX"
            required
          />
          <Select
            label="Qaybta"
            name="department"
            value={formData.department}
            onChange={handleChange}
            options={DEPARTMENTS}
            required
          />
          <Input
            label="Taariikhda uu bilaabay shaqada"
            name="startWorkDate"
            type="date"
            value={formData.startWorkDate}
            onChange={handleChange}
          />
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)} fullWidth>
              Ka noqo
            </Button>
            <Button type="submit" loading={submitting} fullWidth>
              {editingEmployee ? 'Kaydi' : 'Ku dar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Tirtir Shaqaalaha"
        size="sm"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">👤</span>
          </div>
          <p className="text-gray-600 mb-6">
            Ma hubtaa inaad rabto inaad tirtirto <strong>{editingEmployee?.name}</strong>?
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} fullWidth>
              Maya
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={submitting} fullWidth>
              Haa, Tirtir
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Employees;